// Редовете на „Изписване от склад" при разнасяне: продукт на един склад,
// продукт, разпределен по няколко склада (подредове с въвеждаемо количество —
// последният склад е „балансьор" и поема остатъка), и подредът на заместител
// „⇄ вместо X". Колони: Продукт · Склад · За изписване · Наличност · След ·
// Нето · Бруто. „След" е прогноза и може да е под нула — тогава е червено.
// Редовете са с височината на справките (`POSTING_ROW` — 34px с мишка, 44px с
// пръст, `postingDensity.ts`), ЕДИН ред съдържание: бележката („⇄ покрити…",
// „вместо X") е суфикс след името, а мярката е само в „За изписване" —
// „Наличност" и „След" са в същата мярка. Продукт без склад е с кехлибарена
// точка и подсказка в празния склад — липсва настройка, не недостиг.
import type { ReactNode } from "react";
import { InputNumber, Select, Tooltip } from "antd";
import { useTranslation } from "react-i18next";
import type { ReportPostingPreview } from "../../../../api/inventory";
import { formatQty } from "../../components/qty";
import { formatMoney } from "../../components/numbers";
import { StatusDot, type PillTone } from "../../components/list/ListBits";
import { TruncatedText } from "../../components/list/TruncatedText";
import { QTY_EPSILON, WRITEOFF_GRID_CLASS } from "./reportPostingShared";
import { useWarehouseCost } from "./reportPostingCost";
import { POSTING_ROW } from "./postingDensity";
import type { ReportPostingWorksheetState } from "./ReportPostingWorksheetState";

// ─── Клетки ─────────────────────────────────────────────────────────────────
function QtyCell({
  value,
  unit,
  decimals,
  tone = "default",
}: {
  value: number;
  // само в „За изписване" — другите колони са в същата мярка
  unit?: string | null;
  decimals: number;
  tone?: "default" | "strong" | "muted" | "danger";
}) {
  const valueClass = {
    default: "text-ink-2",
    strong: "font-semibold text-ink",
    muted: "text-faint",
    danger: "font-semibold text-danger-ink",
  }[tone];
  // минусът е типографски „−", не тире
  const text = formatQty(Math.abs(value), decimals);
  return (
    <div className={`truncate text-right font-sheet text-[13px] leading-5 tabular-nums ${valueClass}`}>
      {value < -QTY_EPSILON ? `−${text}` : text}
      {unit ? <span className="text-xs font-normal text-faint"> {unit}</span> : null}
    </div>
  );
}

function MoneyCell({ value, strong = false }: { value: number | null; strong?: boolean }) {
  return (
    <div
      className={`text-right font-sheet text-[13px] leading-5 tabular-nums ${
        value == null ? "text-faint" : strong ? "font-semibold text-ink" : "text-ink-2"
      }`}
    >
      {value != null ? formatMoney(value) : "—"}
    </div>
  );
}

// Името с точка за статус и бележката като суфикс на СЪЩИЯ ред (покрито със
// заместител / вместо X). Отрязва се краят — бележката, не името; цялото е в
// подсказката.
export function NameCell({
  name,
  tone,
  note,
  indent = false,
}: {
  name: string;
  tone: PillTone;
  note?: string;
  indent?: boolean;
}) {
  return (
    <div className={`flex min-w-0 items-center gap-2 ${indent ? "pl-4" : ""}`}>
      <StatusDot tone={tone} />
      <TruncatedText
        text={note ? `${name} · ${note}` : name}
        className="text-[13px] font-medium leading-5 text-ink"
      >
        {name}
        {note && <span className="text-xs font-normal text-ok-deep"> · {note}</span>}
      </TruncatedText>
    </div>
  );
}

// ─── Едно-складов продукт = един ред ────────────────────────────────────────
export function SingleWarehouseRow({
  productId,
  unit,
  decimals,
  warehouseId,
  quantity,
  available,
  after,
  grossFactor,
  nameCell,
  warehouseSelect,
  fullyCovered,
  onCost,
}: {
  productId: number;
  unit: string | null;
  decimals: number;
  warehouseId: number | null; // null = без избран склад (станцията няма склад)
  quantity: number;
  available: number;
  after: number;
  grossFactor: number;
  nameCell: ReactNode;
  warehouseSelect: ReactNode;
  fullyCovered: boolean;
  onCost: (value: number | null) => void;
}) {
  const { sum } = useWarehouseCost(productId, warehouseId, quantity, onCost);
  const noWarehouse = warehouseId == null;
  return (
    <div className={`${WRITEOFF_GRID_CLASS} ${POSTING_ROW} border-t border-shade-1`}>
      {nameCell}
      {warehouseSelect}
      <QtyCell
        value={quantity}
        unit={unit}
        decimals={decimals}
        tone={fullyCovered ? "muted" : "strong"}
      />
      {noWarehouse ? <MoneyCell value={null} /> : <QtyCell value={available} decimals={decimals} />}
      {noWarehouse ? (
        <MoneyCell value={null} />
      ) : (
        <QtyCell
          value={after}
          decimals={decimals}
          tone={after < -QTY_EPSILON ? "danger" : "strong"}
        />
      )}
      <MoneyCell value={sum} />
      <MoneyCell value={sum != null ? sum * grossFactor : null} strong />
    </div>
  );
}

// ─── Подред при разпределяне по >1 склад ────────────────────────────────────
export function SplitWarehouseRow({
  productId,
  decimals,
  warehouseId,
  warehouseName,
  rawQuantity,
  effective,
  isBalancer,
  available,
  grossFactor,
  onQty,
  onCost,
}: {
  productId: number;
  decimals: number;
  warehouseId: number;
  warehouseName: string;
  rawQuantity: number | null;
  effective: number;
  isBalancer: boolean;
  available: number;
  grossFactor: number;
  onQty: (value: number | null) => void;
  onCost: (value: number | null) => void;
}) {
  const { sum } = useWarehouseCost(productId, warehouseId, effective, onCost);
  const after = available - effective;
  return (
    <div className={`${WRITEOFF_GRID_CLASS} ${POSTING_ROW} border-t border-shade-1 bg-shade-1`}>
      <TruncatedText text={warehouseName} className="pl-4 text-[12.5px] text-muted">
        ↳ <span className="text-body">{warehouseName}</span>
      </TruncatedText>
      <div />
      <InputNumber
        size="small"
        value={isBalancer ? effective : rawQuantity}
        onChange={(value) => onQty(value as number | null)}
        min={0}
        precision={decimals}
        controls={false}
        placeholder={decimals > 0 ? `0,${"0".repeat(decimals)}` : "0"}
        status={after < -QTY_EPSILON || (isBalancer && effective < -QTY_EPSILON) ? "error" : undefined}
        className="w-full"
      />
      <QtyCell value={available} decimals={decimals} />
      <QtyCell
        value={after}
        decimals={decimals}
        tone={after < -QTY_EPSILON ? "danger" : "strong"}
      />
      <MoneyCell value={sum} />
      <MoneyCell value={sum != null ? sum * grossFactor : null} strong />
    </div>
  );
}

// ─── Подред на заместител („⇄ вместо X") ───────────────────────────────────
// Донорът се изписва в отделен документ „Замяна" (BE), затова тук е само
// количеството и остатъкът му — парите са в онзи документ, не в разнасянето.
export function DonorRow({
  name,
  recipientName,
  warehouseName,
  quantity,
  stock,
  unit,
  decimals,
}: {
  name: string;
  recipientName: string;
  warehouseName: string;
  quantity: number;
  stock: number;
  unit: string | null;
  decimals: number;
}) {
  const { t } = useTranslation();
  const after = stock - quantity;
  return (
    <div className={`${WRITEOFF_GRID_CLASS} ${POSTING_ROW} border-t border-shade-1 bg-ok-tint/60`}>
      <NameCell
        name={name}
        tone="ok"
        indent
        note={t("inventoryPage.postingInsteadOf", { name: recipientName })}
      />
      <TruncatedText text={warehouseName} className="text-[13px] text-body" />
      <QtyCell value={quantity} unit={unit} decimals={decimals} tone="strong" />
      <QtyCell value={stock} decimals={decimals} />
      <QtyCell
        value={after}
        decimals={decimals}
        tone={after < -QTY_EPSILON ? "danger" : "strong"}
      />
      <MoneyCell value={null} />
      <MoneyCell value={null} />
    </div>
  );
}

// ─── Всички редове на един продукт ──────────────────────────────────────────
export function ProductWriteoffRows({
  product,
  state,
  grossFactor,
  decimals,
  warehouseAddon,
}: {
  product: ReportPostingPreview["products"][number];
  state: ReportPostingWorksheetState;
  grossFactor: number;
  decimals: number;
  // Слот до склада на продукта — малък белег или действие (напр. „склад на
  // станцията", „Винаги от <склад>"); без него клетката е само изборът на склад
  warehouseAddon?: ReactNode;
}) {
  const { t } = useTranslation();
  const allocations = state.allocations[product.productId] ?? [];
  const warehouseIds = allocations.map((allocation) => allocation.warehouseId);
  const isSplit = warehouseIds.length > 1;
  const balancer = state.balancerOf(product.productId);
  const toAllocate = state.toAllocateOf(product.productId);
  const shortage = state.shortageByProduct.get(product.productId) ?? 0;
  const covered = state.coveredOf.get(product.productId) ?? 0;
  const warehouseOptions = state.warehouseOptionsOf.get(product.productId) ?? [];
  const productCost = allocations.reduce(
    (sum, allocation) => sum + (state.costs[allocation.id] ?? 0),
    0,
  );

  // Без склад (няма предизбор и не е избран) — кехлибарено: липсва настройка,
  // не недостиг; разнасянето го спира, докато не се избере склад
  const noWarehouse = warehouseIds.length === 0 && toAllocate > QTY_EPSILON;
  const tone: PillTone =
    shortage > 0 ? "danger" : noWarehouse ? "brand" : covered > 0 ? "ok" : "neutral";
  const nameCell = (
    <NameCell
      name={product.productName}
      tone={tone}
      note={
        covered > 0
          ? t("inventoryPage.postingCoveredNote", {
              qty: formatQty(covered, decimals),
              unit: product.unit ?? "",
            })
          : undefined
      }
    />
  );

  // Складът е плосък (без рамка) — чете се като текст, рамка се появява на
  // hover; до него — слотът `warehouseAddon`. Празният склад казва „Изберете
  // склад" и в подсказка откъде се задава складът за постоянно.
  const warehouseSelect = (
    <Tooltip title={noWarehouse ? t("inventoryPage.postingNoWarehouseHint") : undefined}>
      <div className="flex min-w-0 items-center gap-1">
        <Select
          mode="multiple"
          size="small"
          variant="borderless"
          value={warehouseIds}
          onChange={(value) => state.setWarehouses(product.productId, value as number[])}
          options={warehouseOptions.map((option) => ({ value: option.value, label: option.label }))}
          maxTagCount="responsive"
          removeIcon={warehouseIds.length > 1 ? undefined : null}
          suffixIcon={null}
          placeholder={t("inventoryPage.postingPickWarehouse")}
          popupMatchSelectWidth={200}
          className={`pst-wh min-w-0 flex-1 ${noWarehouse ? "pst-wh-missing" : ""}`}
        />
        {warehouseAddon}
      </div>
    </Tooltip>
  );

  if (!isSplit) {
    const allocation = allocations[0];
    const warehouseId = warehouseIds[0] ?? null;
    return (
      <SingleWarehouseRow
        productId={product.productId}
        unit={product.unit}
        decimals={decimals}
        warehouseId={warehouseId}
        quantity={toAllocate}
        available={warehouseId != null ? state.availableOf(product.productId, warehouseId) : 0}
        after={warehouseId != null ? state.afterOf(product.productId, warehouseId) : 0}
        grossFactor={grossFactor}
        nameCell={nameCell}
        warehouseSelect={warehouseSelect}
        fullyCovered={covered > 0 && toAllocate <= QTY_EPSILON}
        onCost={(value) => state.setAllocationCost(allocation?.id ?? -1, value)}
      />
    );
  }

  const overAllocated =
    balancer != null && state.effectiveQtyOf(product.productId, balancer) < -QTY_EPSILON;
  return (
    <div>
      <div className={`${WRITEOFF_GRID_CLASS} ${POSTING_ROW} border-t border-shade-1`}>
        {nameCell}
        {warehouseSelect}
        <QtyCell value={toAllocate} unit={product.unit} decimals={decimals} tone="strong" />
        <div className="col-span-2 text-right text-[11.5px] leading-5 text-muted">
          {t("inventoryPage.postingPerWarehouse")}
        </div>
        <MoneyCell value={productCost > 0 ? productCost : null} />
        <MoneyCell value={productCost > 0 ? productCost * grossFactor : null} strong />
      </div>
      {allocations.map((allocation) => (
        <SplitWarehouseRow
          key={allocation.id}
          productId={product.productId}
          decimals={decimals}
          warehouseId={allocation.warehouseId}
          warehouseName={
            warehouseOptions.find((option) => option.value === allocation.warehouseId)?.label ??
            `#${allocation.warehouseId}`
          }
          rawQuantity={allocation.quantity}
          effective={state.effectiveQtyOf(product.productId, allocation.warehouseId)}
          isBalancer={allocation.warehouseId === balancer}
          available={state.availableOf(product.productId, allocation.warehouseId)}
          grossFactor={grossFactor}
          onQty={(value) =>
            state.setAllocationQuantity(product.productId, allocation.warehouseId, value)
          }
          onCost={(value) => state.setAllocationCost(allocation.id, value)}
        />
      ))}
      {overAllocated && (
        <div className="border-t border-shade-1 px-3.5 py-1 text-[11.5px] font-medium leading-5 text-danger-ink">
          {t("inventoryPage.postingSplitMismatch")}
        </div>
      )}
    </div>
  );
}
